import React, { useState, useRef, useEffect } from 'react';
import { Amplify, Auth, Storage, I18n } from 'aws-amplify';
import {
    Authenticator,
    useTheme,
    View,
    Image,
    Text,
    Heading
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import {
    ContentLayout,
    Header,
    SpaceBetween,
    Button,
    Alert,
    ProgressBar,
    FormField,
    TokenGroup,
    Container,
    TopNavigation
} from "@cloudscape-design/components";
import './App.css';
import './SafeBoxTheme.js'
import './Translations.js'
import awsconfig from './aws-exports';

I18n.setLanguage('es');
Amplify.configure(awsconfig);

const year = new Date().getFullYear();

function formatBytes(a, b = 2, k = 1024) {
    let d = Math.floor(Math.log(a) / Math.log(k));
    return 0 === a ? "0 Bytes" : parseFloat((a / Math.pow(k, d)).toFixed(Math.max(0, b))) + " " + ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"][d];
}

const Content = () => {
    const hiddenFileInput = useRef(null);

    const [visibleAlert, setVisibleAlert] = useState(false);
    const [uploadList, setUploadList] = useState([]);
    const [fileList, setFileList] = useState([]);
    const [historyList, setHistoryList] = useState([]);
    const [historyCount, setHistoryCount] = useState(0);

    const [username, setUsername] = useState('');
    const [userEmail, setUsermail] = useState('');
    const [userProfile, setUserprofile] = useState('');

    useEffect(() => {
        async function fetchUserInfo() {
            try {
                // Get the current authenticated user's information.
                const userInfo = await Auth.currentUserInfo();
                const userAttributes = userInfo.attributes;

                setUsername(userInfo.username);
                setUsermail(userAttributes.email);
                setUserprofile(userAttributes.profile);
            } catch (error) {
                console.error('Error al obtener la información del usuario:', error);
            }
        }

        fetchUserInfo();
    }, []);

    const handleClick = () => {
        // This avoids errors when selecting the same files multiple times
        hiddenFileInput.current.value = "";
        hiddenFileInput.current.click();
    };
    const handleChange = e => {
        e.preventDefault();
        let i, tempUploadList = [];

        for (i = 0; i < e.target.files.length; i++) {
            tempUploadList.push({
                label: e.target.files[i].name,
                labelTag: formatBytes(e.target.files[i].size),
                description: 'File type: ' + e.target.files[i].type,
                icon: 'file',
                id: i
            })
        }

        setUploadList(tempUploadList);
        setFileList(e.target.files);
    };

    function progressBarFactory(fileObject) {
        let localHistory = historyList;
        const id = localHistory.length;

        localHistory.push({
            id: id,
            percentage: 0,
            filename: fileObject.name,
            filetype: fileObject.type,
            filesize: formatBytes(fileObject.size),
            status: 'in-progress'
        });

        setHistoryList(localHistory);

        return (progress) => {
            let tempHistory = historyList.slice();
            const percentage = Math.round((progress.loaded / progress.total) * 100);
            tempHistory[id].percentage = percentage;

            if (percentage === 100) {
                tempHistory[id]['status'] = 'success';
            }

            setHistoryList(tempHistory);
        };
    }

    const handleUpload = () => {
        if (uploadList.length === 0) {
            setVisibleAlert(true);
        } else {
            console.log('Uploading files to S3');
            let i, progressBar = [], uploadCompleted = [];

            for (i = 0; i < uploadList.length; i++) {
                // If the user has removed some items from the Upload list, we need to correctly reference the file
                const id = uploadList[i].id;

                progressBar.push(progressBarFactory(fileList[id]));

                setHistoryCount(historyCount + 1);

                const prefix = `${userProfile}/`

                uploadCompleted.push(Storage.put(prefix + fileList[id].name, fileList[id], {
                    progressCallback: progressBar[i],
                    level: "public",
                    metadata: {
                        "user_owner": username,
                        "user_mail": userEmail,
                        "user_profile": userProfile
                    }
                }).then(result => {
                    // TODO: Trying to remove items from the upload list as they complete. Maybe not work correctly
                    // setUploadList(uploadList.filter(item => item.label !== result.key));
                    console.log(`Completed the upload of ${result.key}`);
                })
                );
            }

            // When you finish the loop, all items should be removed from the upload list
            Promise.all(uploadCompleted)
                .then(() => setUploadList([]));
        }
    }

    const handleDismiss = (itemIndex) => {
        setUploadList([
            ...uploadList.slice(0, itemIndex),
            ...uploadList.slice(itemIndex + 1)
        ]);
    };

    const List = ({ list }) => (
        <>
            {list.map((item) => (
                <ProgressBar
                    key={item.id}
                    status={item.status}
                    value={item.percentage}
                    variant="standalone"
                    additionalInfo={item.filesize}
                    description={item.filetype}
                    label={item.filename}
                />
            ))}
        </>
    );

    return (
        <div className="container">
            <ContentLayout>
                <SpaceBetween size="l">
                    <Container
                        header={
                            <Header variant="h2">
                                Selecciona los documentos
                            </Header>
                        }
                    >
                        {
                            <div>
                                {visibleAlert &&
                                    <Alert
                                        onDismiss={() => setVisibleAlert(false)}
                                        dismissAriaLabel="Cerrar alerta"
                                        dismissible
                                        type="error"
                                        header="No hay archivos seleccionados"
                                    >
                                        Selecciona los documentos.
                                    </Alert>
                                }
                                <FormField
                                    description='Pulsa en el botón y selecciona los documentos.'
                                />

                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button onClick={handleClick}
                                        iconAlign="left"
                                        iconName="upload"
                                    >
                                        Elegir archivo[s]
                                    </Button>
                                    <input
                                        type="file"
                                        ref={hiddenFileInput}
                                        onChange={handleChange}
                                        style={{ display: 'none' }}
                                        multiple
                                    />
                                    <Button variant="primary" onClick={handleUpload}>Subir</Button>
                                </SpaceBetween>

                                <TokenGroup
                                    onDismiss={({ detail: { itemIndex } }) => {
                                        handleDismiss(itemIndex)
                                    }}
                                    items={uploadList}
                                    alignment="vertical"
                                    limit={10}
                                />
                            </div>
                        }
                    </Container>
                    <Container
                        header={
                            <Header variant="h2">
                                Historial
                            </Header>
                        }
                    >
                        <List list={historyList} />
                    </Container>
                </SpaceBetween>
            </ContentLayout>
        </div>
    );
};

const components = {
    Header() {
        const { tokens } = useTheme();
        return (
            <View textAlign="center" padding={tokens.space.xl}>
                <Image
                    alt="Generacion Digital"
                    src="./generaciond_logo.png"
                    objectFit="initial"
                    objectPosition="30% 30%"
                    backgroundColor="initial"
                    opacity="100%"
                    height="20%"
                    width="40%"
                />
            </View>
        );
    },

    Footer() {
        const { tokens } = useTheme();

        return (

            <View textAlign="center" padding={tokens.space.xl}>
                <Image
                    alt="EOI - Escuela de Organizacion Industrial"
                    src="./safebox_logo.png"
                    objectFit="initial"
                    objectPosition="30% 30%"
                    backgroundColor="initial"
                    opacity="100%"
                /> 
                <Text color={tokens.colors.neutral[80]}>
                    &copy; {year} EOI - Escuela de Organizacion Industrial - Todos los derechos reservados.
                </Text>
            </View>
        );
    },

    SignIn: {
        Header() {
            const { tokens } = useTheme();

            return (
                <>
                    <Heading className="heading-signIn"
                        padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                        level={4}
                    >
                        Programa Agentes del cambio
                    </Heading>
                </>
            );
        },
    }
};

function App() {
    const navbarItemClick = e => {
        console.log(e);
        if (e.detail.id === 'signout') {
            Auth.signOut().then(() => {
                window.location.reload();
            });
        }
    }

    return (
        <Authenticator hideSignUp components={components}>
            {({ signOut, user }) => (
                <>
                    <div id="navbar" className="navbar">
                        <TopNavigation
                            identity={{
                                title: "Programa Agentes del cambio",
                                logo: { src: "./generaciond_logo_transparent.png", alt: "Generacion Digital" }
                            }}
                            utilities={[
                                {
                                    type: "button",
                                    text: "Programas",
                                    href: "https://safebox.eoi.es/",
                                    external: true
                                },
                                {
                                    type: "menu-dropdown",
                                    text: user.attributes.email,
                                    description: "Entidad: " + user.attributes.profile,
                                    iconName: "user-profile",
                                    onItemClick: navbarItemClick,
                                    items: [
                                        { id: "signout", text: "Cerrar sesión" }
                                    ]
                                }
                            ]}
                        />
                    </div>
                    <Content />
                </>
            )}
        </Authenticator>
    );
}

export default App;
